import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { invoiceLog } from "./invoiceLogger";
import {
  Bug, ChevronDown, ChevronUp, AlertTriangle, Info,
  CheckCircle2, Clock, Trash2, Download
} from "lucide-react";

const LEVEL_CFG = {
  error: { color: "text-red-600 bg-red-50",     icon: AlertTriangle, badge: "bg-red-100 text-red-700" },
  warn:  { color: "text-amber-600 bg-amber-50",  icon: AlertTriangle, badge: "bg-amber-100 text-amber-700" },
  info:  { color: "text-blue-600 bg-blue-50",    icon: Info,          badge: "bg-blue-100 text-blue-700" },
  debug: { color: "text-gray-500 bg-gray-50",    icon: Info,          badge: "bg-gray-100 text-gray-600" },
};

export default function DiagnosticsPanel() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [, forceUpdate] = useState(0);

  const report = invoiceLog.getReport();
  const entries = filter === "all" ? report.entries : report.entries.filter(e => e.level === filter);

  const exportLog = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `invoice_log_${report.sessionId}.json`; a.click();
  };

  const clearLog = () => {
    invoiceLog.clear();
    forceUpdate(n => n + 1);
  };

  const errCount  = report.errors.length;
  const warnCount = report.warnings.length;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-600">Diagnòstic del pipeline</span>
          {errCount > 0 && <Badge className="bg-red-100 text-red-700 border-0 text-xs">{errCount} errors</Badge>}
          {warnCount > 0 && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">{warnCount} avisos</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{report.totalEntries} entrades</span>
          {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-3">
          {/* Controls */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1.5 flex-wrap">
              {["all", "error", "warn", "info", "debug"].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    filter === f ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={exportLog}>
                <Download className="w-3 h-3" /> Exportar
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-500 hover:text-red-600" onClick={clearLog}>
                <Trash2 className="w-3 h-3" /> Netejar
              </Button>
            </div>
          </div>

          {/* Session info */}
          <div className="text-[10px] font-mono text-gray-400 bg-gray-50 px-2 py-1.5 rounded-lg">
            Session: {report.sessionId} · {report.totalEntries} log entries
          </div>

          {/* Log entries */}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {entries.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">Sense entrades de diagnòstic</p>
            )}
            {entries.map((e, i) => {
              const cfg = LEVEL_CFG[e.level] || LEVEL_CFG.debug;
              const Icon = cfg.icon;
              return (
                <div key={`entry-${i}`} className={`flex items-start gap-2 px-2 py-1.5 rounded-lg text-xs ${cfg.color}`}>
                  <Icon className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${cfg.badge} border-0 text-[9px] py-0`}>{e.level}</Badge>
                      <span className="font-semibold text-[10px] text-gray-500">[{e.stage}]</span>
                      <span className="font-medium">{e.message}</span>
                    </div>
                    {e.data && (
                      <pre className="text-[9px] text-gray-400 mt-0.5 truncate font-mono">
                        {JSON.stringify(e.data).slice(0, 200)}
                      </pre>
                    )}
                  </div>
                  <span className="text-[9px] text-gray-400 flex-shrink-0 font-mono">
                    {e.ts.slice(11, 19)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}